import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Button,
    Stack,
    CircularProgress,
    Alert,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LockResetIcon from "@mui/icons-material/LockReset";
import BadgeIcon from "@mui/icons-material/Badge";
import SaveIcon from "@mui/icons-material/Save";

import { MIN_PASSWORD_LENGTH } from "../../constants";
import { esUsernameValido, esEmailValido, esPasswordValido, coincidePassword } from "../../utils/validators";

const CAMPOS = ["username", "email", "password", "confirmarPassword"];

const labelSx = {
    fontWeight: 600,
    mb: 1,
    color: "#495057",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
};

const inputSx = { "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } };

/**
 * Formulario controlado de alta/edición de usuario (username, email,
 * password/confirmación y rol). No incluye Dialog propio ni llama a
 * UsuariosService directamente: al validar correctamente, entrega los datos
 * a través de onSubmit para que ModalUsuario (TASK-009) decida cómo
 * persistirlos con useUsuarios.
 */
const UsuarioForm = ({ usuario, roles = [], loadingRoles = false, errorRoles = null, loading = false, onSubmit }) => {
    const esEdicion = Boolean(usuario?.id);

    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmarPassword, setConfirmarPassword] = useState("");
    const [rolId, setRolId] = useState("");
    const [tocado, setTocado] = useState({});

    // Precarga los campos al editar un usuario existente; en alta llega como
    // null/undefined y limpia. El password NUNCA se precarga (ni en edición):
    // vacío significa "no cambiar la contraseña actual", ver validators.js
    useEffect(() => {
        setUsername(usuario?.username ?? "");
        setEmail(usuario?.email ?? "");
        setPassword("");
        setConfirmarPassword("");
        setRolId(usuario?.rol?.id ?? "");
        setTocado({});
    }, [usuario]);

    const errores = useMemo(
        () => ({
            username: esUsernameValido(username) ? "" : "El usuario es obligatorio",
            email: esEmailValido(email) ? "" : "Introduce un email válido",
            password: esPasswordValido(password, { esEdicion })
                ? ""
                : esEdicion
                ? `Si cambias la contraseña, debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`
                : `La contraseña es obligatoria (mínimo ${MIN_PASSWORD_LENGTH} caracteres)`,
            confirmarPassword: coincidePassword(password, confirmarPassword) ? "" : "Las contraseñas no coinciden",
        }),
        [username, email, password, confirmarPassword, esEdicion]
    );

    const formularioValido = useMemo(() => Object.values(errores).every((error) => !error), [errores]);

    // DECISION: el botón NO se deshabilita por errores de campos que el usuario
    // todavía no ha tocado (p.ej. al abrir el formulario en alta, con todo
    // vacío) porque se percibe como un formulario roto; solo cuenta un campo
    // ya tocado que sigue en error. La validación completa (formularioValido)
    // se aplica de todos modos al enviar, en handleSubmit, como red de
    // seguridad si el usuario nunca llega a tocar un campo obligatorio.
    const hayErroresVisibles = useMemo(
        () => Object.keys(tocado).some((campo) => tocado[campo] && errores[campo]),
        [tocado, errores]
    );

    const marcarTocado = (campo) => setTocado((prev) => ({ ...prev, [campo]: true }));

    const mostrarError = (campo) => Boolean(tocado[campo] && errores[campo]);

    const handleCambio = (campo, setter) => (event) => {
        setter(event.target.value);
        marcarTocado(campo);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!formularioValido) {
            // El botón ya queda deshabilitado si el formulario no es válido; esto
            // revela cualquier error que quedara oculto por no haber tocado ese campo
            setTocado(CAMPOS.reduce((acc, campo) => ({ ...acc, [campo]: true }), {}));
            return;
        }

        onSubmit({
            username: username.trim(),
            email: email.trim(),
            password,
            rolId: rolId === "" ? null : rolId,
        });
    };

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
            <Stack spacing={3}>
                {/* Usuario */}
                <Box>
                    <Typography variant="body2" sx={labelSx}>
                        <PersonIcon sx={{ fontSize: 18 }} />
                        Usuario *
                    </Typography>
                    <TextField
                        id="username"
                        fullWidth
                        variant="outlined"
                        placeholder="Ej: jperez"
                        value={username}
                        onChange={handleCambio("username", setUsername)}
                        onBlur={() => marcarTocado("username")}
                        error={mostrarError("username")}
                        helperText={mostrarError("username") ? errores.username : ""}
                        disabled={loading}
                        sx={inputSx}
                    />
                </Box>

                {/* Email */}
                <Box>
                    <Typography variant="body2" sx={labelSx}>
                        <EmailIcon sx={{ fontSize: 18 }} />
                        Email *
                    </Typography>
                    <TextField
                        id="email"
                        fullWidth
                        variant="outlined"
                        placeholder="Ej: jperez@mandatelo.com"
                        value={email}
                        onChange={handleCambio("email", setEmail)}
                        onBlur={() => marcarTocado("email")}
                        error={mostrarError("email")}
                        helperText={mostrarError("email") ? errores.email : ""}
                        disabled={loading}
                        sx={inputSx}
                    />
                </Box>

                {/* Password */}
                <Box>
                    <Typography variant="body2" sx={labelSx}>
                        <LockIcon sx={{ fontSize: 18 }} />
                        Contraseña {esEdicion ? "" : "*"}
                    </Typography>
                    <TextField
                        id="password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        placeholder={esEdicion ? "Déjalo vacío para no cambiarla" : "Mínimo 6 caracteres"}
                        value={password}
                        onChange={handleCambio("password", setPassword)}
                        onBlur={() => marcarTocado("password")}
                        error={mostrarError("password")}
                        helperText={
                            mostrarError("password")
                                ? errores.password
                                : esEdicion
                                ? "Déjalo vacío para mantener la contraseña actual"
                                : `Mínimo ${MIN_PASSWORD_LENGTH} caracteres`
                        }
                        disabled={loading}
                        sx={inputSx}
                    />
                </Box>

                {/* Confirmar password */}
                <Box>
                    <Typography variant="body2" sx={labelSx}>
                        <LockResetIcon sx={{ fontSize: 18 }} />
                        Confirmar contraseña {esEdicion ? "" : "*"}
                    </Typography>
                    <TextField
                        id="confirmar-password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        placeholder="Repite la contraseña"
                        value={confirmarPassword}
                        onChange={handleCambio("confirmarPassword", setConfirmarPassword)}
                        onBlur={() => marcarTocado("confirmarPassword")}
                        error={mostrarError("confirmarPassword")}
                        helperText={mostrarError("confirmarPassword") ? errores.confirmarPassword : ""}
                        disabled={loading || password === ""}
                        sx={inputSx}
                    />
                </Box>

                {/* Rol */}
                <Box>
                    <Typography variant="body2" sx={labelSx}>
                        <BadgeIcon sx={{ fontSize: 18 }} />
                        Rol
                    </Typography>
                    <FormControl fullWidth disabled={loadingRoles || loading}>
                        <Select
                            id="rol"
                            displayEmpty
                            value={rolId}
                            onChange={handleCambio("rolId", setRolId)}
                            sx={{ backgroundColor: "#fff" }}
                            renderValue={(valorSeleccionado) => {
                                if (loadingRoles) return "Cargando roles...";
                                if (valorSeleccionado === "") return "Sin rol asignado";

                                const rolSeleccionado = roles.find((rol) => rol.id === valorSeleccionado);
                                return rolSeleccionado?.name ?? "Sin rol asignado";
                            }}
                        >
                            <MenuItem value="">Sin rol asignado</MenuItem>
                            {roles.map((rol) => (
                                <MenuItem key={rol.id} value={rol.id}>
                                    {rol.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {errorRoles && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: 12 }}>
                            {errorRoles}. Puedes guardar el usuario y asignarle el rol más tarde.
                        </Alert>
                    )}
                </Box>

                {/* Submit */}
                <Box sx={{ pt: 1 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        startIcon={
                            loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />
                        }
                        disabled={loading || hayErroresVisibles}
                        sx={{
                            backgroundColor: "#cc071e",
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.25,
                            fontSize: 15,
                            "&:hover": { backgroundColor: "#a30618" },
                            "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                        }}
                    >
                        {loading ? "Guardando..." : esEdicion ? "Guardar cambios" : "Crear usuario"}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default UsuarioForm;
